/**
 * Clock helpers shared by the irrigation live pages.
 *
 * Lifted out of IrrigationOverview when the per-farm page started answering the
 * same question — "what time does it come on?" — so both pages can never drift
 * into phrasing it differently.
 */

import { formatTimeInTz, getTodayInTz, tzOffsetSec } from './timezone';

/** YYYY-MM-DD for an instant, in the given timezone — for same-day comparison. */
export function dayKeyInTz(ms: number, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms));
}

/**
 * The clock time a queued run starts, in the org's timezone — "4:30 AM", or
 * "Tomorrow 4:30 AM" / "Tue 4:30 AM" when it falls past midnight. A countdown
 * alone doesn't answer "what time does it come on?", which is the question
 * anyone planning around the shift is actually asking.
 */
export function formatStartClock(startsAtMs: number, timezone: string): string {
  const time = formatTimeInTz(new Date(startsAtMs).toISOString(), timezone);
  const day = dayKeyInTz(startsAtMs, timezone);
  if (day === getTodayInTz(timezone)) return time;
  if (day === dayKeyInTz(Date.now() + 86_400_000, timezone)) return `Tomorrow ${time}`;
  const weekday = new Intl.DateTimeFormat('en-AU', {
    timeZone: timezone,
    weekday: 'short',
  }).format(new Date(startsAtMs));
  return `${weekday} ${time}`;
}

/**
 * The site's UTC offset in seconds — the frame today's programs were selected
 * in, for folding a UTC seconds-from-midnight value with `localDaySec`.
 *
 * THE BUG THIS EXISTS FOR
 *   Two browsers, the same bundle, the same live-map payload, and two
 *   different answers about what was watering.
 *
 *   The fold offset came from `useOrgTimezone()`, which falls back to
 *   `Intl.DateTimeFormat().resolvedOptions().timeZone` — the viewer's own
 *   machine — whenever the active org membership carries no timezone. Two
 *   people looking at one farm then fold by whatever their operating systems
 *   are set to, and a run the site is genuinely part-way through reads as hours
 *   away on one of them. Switching active org does it too, which is how one
 *   account can disagree with itself.
 *
 * The org's IANA timezone is still preferred when it is genuinely set, because
 * `Intl` tracks the DST changeover and a fixed offset goes an hour out for five
 * months of the year. Failing that, the CONTROLLER's own reported offset is the
 * frame its hardware runs its clock in — not DST-aware, but a property of the
 * site rather than of whoever is looking at it.
 */
export function siteOffsetSec(
  orgTimezone: string | null,
  controllerOffsetSec: number | null | undefined,
  at: Date = new Date()
): number {
  if (orgTimezone) return tzOffsetSec(orgTimezone, at);
  return controllerOffsetSec ?? 0;
}
